// ============================================
// Journey Resume Service
//
// Checks for an existing patient journey on app mount
// and hydrates AppState so the patient resumes where
// they left off. Called once on startup if sessionId
// exists in localStorage.
// ============================================

import { getJourney, loadMessages, type PatientJourney } from './journeyService';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { C_PATIENT_JOURNEYS } from '../../constants/collections';
import type { AppState, ChatMessage, AppScreen } from '../../types';

// ── Result type ──

export interface ResumeResult {
  journey: PatientJourney;
  messages: ChatMessage[];
}

// ── Check + load ──

/** Check if an active journey exists for this sessionId. Returns null if none or complete. */
export async function checkForExistingJourney(sessionId: string): Promise<ResumeResult | null> {
  const journey = await getJourney(sessionId);

  if (!journey) return null;
  if (journey.status === 'complete') return null;

  // Journey is active — load messages
  const messages = await loadMessages(sessionId);

  return { journey, messages };
}

// ── Hydrate ──

/** Map a resumed journey + messages back to partial AppState for the HYDRATE action. */
export function hydrateAppState(
  result: ResumeResult,
): Pick<AppState, 'screen' | 'triageData' | 'chatHistory'> {
  const { journey, messages } = result;

  // Determine which screen to resume to
  const screen = resolveResumeScreen(journey);

  return {
    screen,
    triageData: journey.triageData,
    chatHistory: messages,
  };
}

// ── UID-based lookup (returning patients) ──

/**
 * Find the most recent active journey for an authenticated user.
 * Used when a patient signs in and has no localStorage session ID
 * matching an existing journey. Returns null if no active journey found.
 */
export async function findActiveJourneyByUid(uid: string): Promise<ResumeResult | null> {
  try {
    const q = query(
      collection(db, C_PATIENT_JOURNEYS),
      where('uid', '==', uid),
      where('status', '==', 'active'),
      orderBy('_updatedAt', 'desc'),
      limit(1),
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;

    const data = snap.docs[0].data() as PatientJourney;
    const sessionId = data.sessionId ?? snap.docs[0].id;
    const messages = await loadMessages(sessionId);

    return { journey: { ...data, sessionId }, messages };
  } catch (err) {
    console.warn('[Journey] UID-based resume lookup failed:', err);
    return null;
  }
}

// ── Helpers ──

/** Determine the best screen to resume to based on journey state. */
function resolveResumeScreen(journey: PatientJourney): AppScreen {
  // If they were on chat or later, resume to chat (the main flow hub)
  const step = journey.currentStep;

  switch (step) {
    case 'chat':
    case 'protocol':
    case 'checkout':
      return 'chat';
    case 'processing':
      return 'chat'; // processing is transient, resume to chat
    case 'triage':
      // They started triage but have data — go to chat
      if (journey.triageData) return 'chat';
      return 'landing';
    default:
      return 'landing';
  }
}
